import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Badge, Alert, Form } from 'react-bootstrap';
import { Phone, ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import api from "../../services/api";
import { userMessageOf } from '../../services/apiError';
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import UserLoginUsersPanel from './UserLoginUsersPanel';
import { toast } from 'react-toastify';
import {
  PERMISSION_FLAGS,
  ROLE_FILTERS,
  hasBit,
  withBit,
  nameOf,
  matchesFilters,
  permissionsOf,
  changedLogins,
  applySaved,
} from './userLoginsList.logic';
import styles from './UserLoginsManagement.module.scss';

const getRowId = row => row.uid;

export default function UserLoginsManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // Pending edits, uid -> the login's new permissions; kept apart from the rows so sorting and filtering keep them
  const [edits, setEdits] = useState(() => new Map());
  // Expanded rows as TanStack keeps them ({ [uid]: true }). The table stays mounted through a Refresh, so it is
  // collapsed from here; the users fetched per expanded login are cached beside it (uid -> panel entry).
  const [expanded, setExpanded] = useState({});
  const [usersCache, setUsersCache] = useState(() => new Map());

  const [textFilter, setTextFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const busy = loading || saving;

  useEffect(() => {
    loadList();
  }, []);

  const loadList = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/user-logins');
      setItems(response.data.userLogins);
      setLoadFailed(false);
    } catch (error) {
      // Cleared rather than kept: after a failed Refresh the old list would pass for the current one
      setItems([]);
      setLoadFailed(true);
      toast.error(t('admin.userLoginList.loadError'));
      console.error('Error loading user logins, status:', error?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  // Pending edits are dropped without asking: the badge on Save shows how many there are
  const refresh = () => {
    setEdits(new Map());
    setExpanded({});
    setUsersCache(new Map());
    loadList();
  };

  // TableFull keeps its page in ?page= and never resets it, so a filter applied on a later page would show nothing.
  // Built from the current params: react-router 6.2 has no functional setSearchParams, a callback there wipes the query.
  const resetPage = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const setBit = (row, bit, on) => {
    setEdits(prev => new Map(prev).set(row.uid, withBit(permissionsOf(row, prev), bit, on)));
  };

  // Over every loaded login, not the filtered ones: an edit hidden by a filter is still saved
  const changed = useMemo(() => changedLogins(items, edits), [items, edits]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/settings/admin/user-logins/save', { userLogins: changed });
      setItems(prev => applySaved(prev, changed));
      setEdits(new Map());
      toast.success(t('admin.userLoginList.saveSuccess'));
    } catch (error) {
      toast.error(userMessageOf(error) || t('admin.userLoginList.saveError'));
      console.error('Error saving user login permissions, status:', error?.response?.status);
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(
    () => items.filter(x => matchesFilters(x, textFilter, roleFilter)),
    [items, textFilter, roleFilter]
  );

  const columns = useMemo(() => [
    {
      header: t('admin.userLoginList.table.name'),
      id: 'name',
      accessorFn: nameOf,
      // Cells are nowrap by default: a long name would run under the Email cell and hide the badge
      allowWrap: true,
      cell: ({ row }) => {
        // Only countries with a short code show one; 'not specified' and any country added later stay blank
        const countryCode = t(`countries.codes.${row.original.countryId}`, { defaultValue: '' });
        return (
          <>
            {nameOf(row.original)}{' '}
            {countryCode && <Badge bg="dark" text="light" title={t('admin.userLoginList.country')}>{countryCode}</Badge>}{' '}
            {!row.original.isActive && <Badge bg="secondary">{t('admin.userLoginList.inactive')}</Badge>}
          </>
        );
      },
    },
    {
      header: t('admin.userLoginList.table.email'),
      accessorKey: 'email',
      cell: ({ row }) => (
        <Link to={`/admin/user-login/${row.original.uid}/settings/users`}>{row.original.email}</Link>
      ),
    },
    // Sorted by the loaded value, so ticking a box does not move the row away
    ...PERMISSION_FLAGS.map(({ flag, bit, header }) => ({
      header: t(header),
      id: flag,
      accessorFn: row => hasBit(row.permissions, bit),
      className: styles.center,
      cell: ({ row }) => (
        <Form.Check.Input
          type="checkbox"
          className="m-0"
          aria-label={t(header)}
          checked={hasBit(permissionsOf(row.original, edits), bit)}
          disabled={busy}
          onChange={e => setBit(row.original, bit, e.target.checked)}
        />
      ),
    })),
    {
      header: t('admin.userLoginList.table.users'),
      accessorKey: 'usersCount',
      // The counts are the toggle, so the whole cell opens the users panel under the row
      cell: ({ row }) => (
        <Button
          variant="link"
          className={`p-0 text-decoration-none ${styles.usersToggle}`}
          aria-expanded={row.getIsExpanded()}
          onClick={row.getToggleExpandedHandler()}
        >
          {row.original.usersCount}
          {row.original.groupsCount > 0 && (
            <span className="text-muted">
              ({t('admin.userLoginList.usersPanel.groups')}: {row.original.groupsCount})
            </span>
          )}
          {row.getIsExpanded() ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
        </Button>
      ),
    },
    {
      header: t('admin.userLoginList.table.actions'),
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className={styles.actionsCell}>
          <Link className="btn btn-outline-secondary btn-sm" to={`/admin/push-targets?loginUid=${row.original.uid}`}>
            <Phone className="me-1" />
            {t('admin.userLoginList.phones')}
          </Link>
        </div>
      ),
    },
  ], [t, edits, busy]);

  const renderUsersPanel = row => (
    <UserLoginUsersPanel
      uid={row.original.uid}
      entry={usersCache.get(row.original.uid)}
      setUsersCache={setUsersCache}
    />
  );

  // Only the first load replaces the page: a Refresh keeps the table, and with it the admin's sort
  if (loading && items.length === 0) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.filters}>
          <Form.Control
            type="text"
            value={textFilter}
            onChange={e => { setTextFilter(e.target.value); resetPage(); }}
            placeholder={t('admin.userLoginList.textFilterPlaceholder')}
          />
          <Form.Select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); resetPage(); }}>
            {ROLE_FILTERS.map(r => <option key={r} value={r}>{t(`admin.userLoginList.roleFilter.${r}`)}</option>)}
          </Form.Select>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline-primary" onClick={refresh} disabled={busy}>
            {loading && (<span className="spinner-border spinner-border-sm me-1"></span>)}
            {t('admin.userLoginList.refresh')}
          </Button>
          <Button variant="primary" onClick={save} disabled={changed.length === 0 || busy}>
            {saving && (<span className="spinner-border spinner-border-sm me-1"></span>)}
            {t('admin.userLoginList.saveButton')}
            {changed.length > 0 && (
              <Badge bg="light" text="dark" className="ms-2">
                {t('admin.userLoginList.changedCount', { count: changed.length })}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          {/* A failed load must not present itself as an empty list */}
          <Alert variant={loadFailed ? 'danger' : 'info'}>
            {loadFailed ? t('admin.userLoginList.loadError') : t('admin.userLoginList.noLogins')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {/* Row ids are uids: the data is filtered, so TanStack's index ids would move to another login */}
          <TableFull
            data={filteredItems}
            columns={columns}
            styles={styles}
            allowColumnHeaderSort={true}
            mainTableClassName={styles.table}
            getRowId={getRowId}
            expanded={expanded}
            onExpandedChange={setExpanded}
            renderExpandedRow={renderUsersPanel}
          />
        </div>
      )}
    </div>
  );
}
